'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { CarType, Gender } from '@prisma/client';
import { IconMail } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import type { z } from 'zod';
import FormInput from '@/components/forms/form-input';
import { Chip } from '@/components/ui/chips';
import { Form } from '@/components/ui/form';
import { carRentalBookingSchema } from '@/schemas';
import { api } from '@/server/callers/client';
import { trackEvent } from '@/utils/analytics';
import { usePageViewTracking } from '@/utils/hooks';

type FormData = z.infer<typeof carRentalBookingSchema>;

export default function CarTripForm() {
  // Track page view
  usePageViewTracking();

  // State to track if the driver is required based on car type
  const [isDriverRequired, setIsDriverRequired] = useState(true);
  const [showLicenseField, setShowLicenseField] = useState(false);
  const [hasLicense, setHasLicense] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(carRentalBookingSchema),
    defaultValues: {
      fullName: '',
      gender: 'Male',
      email: '',
      phone: '',
      message: '',
      destinationId: '',
      date: new Date(new Date().setDate(new Date().getDate() + 1)),
      duration: 1,
      carType: CarType.Sedan,
      withDriver: true,
    },
  });

  const destinations = api.destinations.list.useQuery();

  // Watch for changes in carType and withDriver fields
  const carType = useWatch({ control: form.control, name: 'carType' });
  const withDriver = useWatch({ control: form.control, name: 'withDriver' });

  // Update driver requirement when car type changes
  useEffect(() => {
    // Driver is required for Bus types
    const required =
      carType === CarType.Bus21 ||
      carType === CarType.Bus50 ||
      carType === CarType.BusMini;

    // Set driver requirement state
    setIsDriverRequired(required);

    // If driver is required, always set withDriver to true
    if (required) {
      form.setValue('withDriver', true, { shouldValidate: true });
    }

    // Reset license checkbox when car type changes
    setHasLicense(false);

    // Update license field visibility
    const shouldShowLicense = !required && !withDriver;
    setShowLicenseField(shouldShowLicense);
  }, [carType, form]);

  // Watch for withDriver changes to update license field visibility
  useEffect(() => {
    if (!isDriverRequired) {
      setShowLicenseField(!withDriver);
      if (withDriver) {
        setHasLicense(false);
      }
    }
  }, [withDriver, isDriverRequired]);

  const bookMutation = api.carRentals.book.useMutation({
    onSuccess: () => {
      // Track successful submission
      trackEvent({
        action: 'car_rental_form_submit_success',
        category: 'forms',
        label: 'Car Rental Form Success',
        value: 1,
      });

      toast.success(
        'Thank you for your car rental request! We will get back to you soon.',
      );
      form.reset();
      setHasLicense(false);
    },
    onError: (error) => {
      // Track submission error
      trackEvent({
        action: 'car_rental_form_submit_error',
        category: 'forms',
        label: error.message || 'Unknown error',
        value: 1,
      });

      toast.error(error.message || 'Something went wrong. Please try again.');
    },
  });

  // Track form interaction
  const hasInteracted = useRef(false);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (!hasInteracted.current && name && value) {
        hasInteracted.current = true;

        trackEvent({
          action: 'car_rental_form_started',
          category: 'forms',
          label: 'Car Rental Form Interaction',
          value: 1,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const handleSubmit = async (data: FormData) => {
    // Check if license is required but not confirmed
    if (showLicenseField && !hasLicense) {
      toast.error('Please confirm you have a valid driving license.');
      return;
    }

    // Track submission attempt
    trackEvent({
      action: 'car_rental_form_submit',
      category: 'forms',
      label: 'Car Rental Form',
      value: 1,
    });

    bookMutation.mutate(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormInput
          type="text"
          id="fullName"
          label="Full Name"
          placeholder="Your full name"
          form={form}
        />

        <FormInput
          type="select"
          id="gender"
          label="Gender"
          required
          options={Object.values(Gender).map((gender) => ({
            value: gender,
            label: gender,
          }))}
          form={form}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            id="email"
            label="Email"
            type="email"
            required
            placeholder="your.email@example.com"
            form={form}
          />

          <FormInput
            id="phone"
            label="Phone"
            type="tel"
            required
            placeholder="Your phone number"
            form={form}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput id="date" label="Rental Date" type="date" required form={form} />

          <FormInput
            id="duration"
            label="Duration (Days)"
            type="number"
            required
            form={form}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            id="carType"
            label="Car Type"
            type="chips"
            multiSelect={false}
            required
            options={[
              {
                value: 'Sedan',
                label: 'Sedan',
              },
              {
                value: 'Suv',
                label: 'Suv',
              },
              {
                value: 'BusMini',
                label: 'Mini Bus',
              },
              {
                value: 'Bus21',
                label: 'Bus 21 seater',
              },
              {
                value: 'Bus50',
                label: 'Bus 50 seater',
              },
            ]}
            form={form}
          />

          <div className="flex flex-col gap-8">
            <div>
              <label htmlFor="withDriver" className="block text-sm font-medium mb-1">
                With Driver{' '}
                {isDriverRequired && <span className="text-red-500">*</span>}
              </label>

              {isDriverRequired && (
                <div className="text-sm text-muted-foreground mb-2">
                  Driver is required for this car type
                </div>
              )}

              <div id="withDriver" className="flex flex-wrap gap-3">
                <Chip
                  selected={withDriver === true}
                  onToggle={() =>
                    form.setValue('withDriver', true, { shouldValidate: true })
                  }
                  disabled={isDriverRequired}
                  className={isDriverRequired ? 'opacity-50 cursor-not-allowed' : ''}
                >
                  Yes
                </Chip>
                <Chip
                  selected={withDriver === false}
                  onToggle={() =>
                    form.setValue('withDriver', false, { shouldValidate: true })
                  }
                  disabled={isDriverRequired}
                  className={isDriverRequired ? 'opacity-50 cursor-not-allowed' : ''}
                >
                  No
                </Chip>
              </div>
            </div>

            {showLicenseField && (
              <div className="mt-2 flex items-center">
                <input
                  id="hasLicense"
                  type="checkbox"
                  checked={hasLicense}
                  onChange={(e) => setHasLicense(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
                  required
                />
                <label
                  htmlFor="hasLicense"
                  className="ml-2 block text-sm font-medium"
                >
                  I have a valid driving license
                </label>
              </div>
            )}
          </div>
        </div>

        <FormInput
          id="destinationId"
          label="Destination"
          type="select"
          required
          options={
            destinations?.data?.map((destination: { id: string; name: string }) => ({
              value: destination.id,
              label: destination.name,
            })) ?? []
          }
          form={form}
        />

        <FormInput
          id="message"
          label="Message"
          type="textarea"
          required
          placeholder="Additional information about your car rental"
          rows={5}
          form={form}
        />

        {bookMutation.isSuccess && (
          <div className="flex flex-col justify-center items-center gap-2 p-4 bg-green-100 rounded-lg text-green-800">
            <IconMail className="text-green-800" size={90} />
            <p className="text-lg">
              You have successfully submitted your car rental request. Please check
              your email for verification. You can also check your spam folder.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={bookMutation.isPending || (showLicenseField && !hasLicense)}
          className="bg-accent text-black font-medium rounded-lg w-fit px-12 py-3 hover:bg-accent/90 transition-colors disabled:r-rentaopacity-50"
        >
          {bookMutation.isPending ? 'Submitting...' : 'Rent a Car'}
        </button>
      </form>
    </Form>
  );
}
